import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigurationStep } from '~/app/compositions/sidebar/configuration-step/configuration-step';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useMoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { hasTeaserText, SelectionCardNew } from '~/components/selection-card-new/selection-card-new';
import { useActiveCard } from '~/components/slider-panel';
import { RenderHtml } from '~/components/text';
import { ConfiguredCarCatalogImage } from '~/graphql/__generated__/graphql';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useImages } from '~/graphql/hooks/use-images';
import { useSteps } from '~/graphql/hooks/use-steps';
import { CatalogItem } from '~/graphql/mapper';
import { FeatureCategory, FeatureOrPackage, SelectableOption, SelectionCardItem } from '~/graphql/types';
import { OptionsHeader } from '../options-header';

export function Headlights() {
	const { i18n } = useI18n();
	const { headlights } = useCatalog();
	const { config } = useMarketConfig();
	const { feature } = useVATConfig();
	const { isLoading, isItemLoading, isItemChecked, loadingFlags } = useConfigurator();
	const { openMoreInformationDialog } = useMoreInformationDialog();
	const { selectCard } = useActiveCard();
	const { getName } = useDebugMode();
	const { mediaByProfileId } = useImages();
	const [imageUrls, setImageUrls] = useState<(string[] | null | undefined)[] | undefined>([]);
	const [media, setMedia] = useState<Array<Array<ConfiguredCarCatalogImage | null | undefined>>>([]);
	const { step, total } = useSteps('headlights');

	const trackSelectionClickEvent = useCallback(
		(
			eventName: string,
			label: string,
			item: FeatureOrPackage,
			elementName: string,
			value: string,
			selected?: boolean,
			eventAction = EventAction.InternalLink,
		) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction,
				eventName,
				label,
				elementName,
				value,
				category: 'headlights',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[step, total],
	);

	const items = useMemo(() => {
		if (!headlights) return [];

		return headlights.map((item, index): SelectionCardItem => {
			return {
				name: getName(item) ?? item.name,
				description: hasTeaserText(item.__item.texts?.teaserText) ? item.__item.texts?.teaserText : null,
				key: item.pr7,
				loading: isItemLoading(item),
				selected: isItemChecked(item),
				readonly: loadingFlags.checkingConflicts || !item.status.available,
				onSelect: () =>
					selectCard(index, item as CatalogItem, () =>
						trackSelectionClickEvent(
							'click to change configuration',
							item.name || '',
							item.__item,
							'card',
							'selection feature app: headlights: all',
							isItemChecked(item),
						),
					),
				standard: item.status.standard,
				galleryImageUrls: item.galleryImages,
				galleryMedia: item.galleryMedia || [],
				price: config.showPrices
					? {
							totalPrice: item.totalPrice,
							monthlyRate: !config.hideMonthlyRates ? item.monthlyRate : undefined,
							feature,
						}
					: undefined,
				textLink: {
					label: i18n('ngc.actions.moreInfo'),
					onTextLinkClick: () =>
						openMoreInformationDialog(
							item as SelectableOption<FeatureCategory>,
							item.__item,
							() =>
								trackSelectionClickEvent(
									'click to change configuration',
									item.name || '',
									item.__item,
									'button',
									'details layer',
									isItemChecked(item),
								),
							step,
							total,
							'headlights',
						),
				},
				onDescriptionToggle: (showDescription: boolean) =>
					trackSelectionClickEvent(
						`click to ${showDescription ? 'close' : 'open'} details accordion`,
						showDescription ? i18n('ngc.actions.hideDetails') : i18n('ngc.actions.showDetails'),
						item.__item,
						'text link',
						'details accordion',
						undefined,
						EventAction.Content,
					),
				disclaimerHeadline: item.__item.texts?.disclaimerHeadline || undefined,
				disclaimerContent: item.__item.texts?.disclaimerText || undefined,
				disclaimerStartDate: item.__item.texts?.disclaimerStartDate || undefined,
				disclaimerEndDate: item.__item.texts?.disclaimerEndDate || undefined,
			};
		});
	}, [
		headlights,
		getName,
		isItemLoading,
		isItemChecked,
		loadingFlags.checkingConflicts,
		config.showPrices,
		config.hideMonthlyRates,
		feature,
		i18n,
		selectCard,
		trackSelectionClickEvent,
		openMoreInformationDialog,
		step,
		total,
	]);

	useEffect(() => {
		const selectedItems = items?.filter((a) => a.selected);
		const images = selectedItems?.map((a) => a.galleryImageUrls) as string[][];
		const newImages = images?.filter((a) => {
			return !imageUrls?.find((b) => {
				return JSON.stringify(b) === JSON.stringify(a);
			});
		});

		const buffer = structuredClone(imageUrls!).filter((a) =>
			images.find((b) => JSON.stringify(b) === JSON.stringify(a)),
		);

		if (newImages) {
			setImageUrls([...structuredClone(newImages), ...structuredClone(buffer)]);
		}

		const structuredMedia: Array<Array<ConfiguredCarCatalogImage | null | undefined>> = [];

		selectedItems?.forEach((itm) => {
			if (Array.isArray(itm.galleryMedia)) {
				structuredMedia.push(itm.galleryMedia);
			} else {
				structuredMedia.push([itm.galleryMedia]);
			}
		});

		setMedia(structuredMedia);
	}, [JSON.stringify(imageUrls), JSON.stringify(items), JSON.stringify(media)]);

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'headlights',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			}
		},
		[isLoading],
	);

	const categoryDisclaimer = config.categoryDisclaimers?.find((d) => d.step === 'headlights')?.disclaimer;

	return (
		<ConfigurationStep
			ref={impressionTrackingRef}
			hide={items.length === 0}
			id={'headlights'}
			media={
				media && media.length > 0
					? [...media, [mediaByProfileId[ImageViewsEnum.LIGHTS_FRONT_HEADLIGHTS]]]
					: [[mediaByProfileId[ImageViewsEnum.LIGHTS_FRONT_HEADLIGHTS]]]
			}
			headline={<OptionsHeader title={i18n('ngc.equipment.headlights')} step={step} total={total} />}
			disclaimer={categoryDisclaimer}>
			{items.map((item) => (
				<SelectionCardNew key={item.name} item={item}>
					{item.description && <RenderHtml html={item.description} />}
				</SelectionCardNew>
			))}
		</ConfigurationStep>
	);
}
